import React, { useState } from 'react';
import { 
  CloudUpload, 
  Description, 
  Security, 
  AutoAwesome,
  GetApp,
  DragIndicator
} from '@mui/icons-material';
import { useDocumentProcessing } from '../../features/cv-redactor/hooks/useDocumentProcessing';
import ProcessingStatus from '../../features/cv-redactor/components/processing/ProcessingStatus';
import ResultDisplay from '../../features/cv-redactor/components/processing/ResultDisplay';
import { Alert } from '@mui/material';
import './CenterWorkspace.css';

interface CenterWorkspaceProps {
  children?: React.ReactNode;
}

const CenterWorkspace: React.FC<CenterWorkspaceProps> = ({ children }) => {
  const [dragActive, setDragActive] = useState(false);
  const {
    currentStep,
    isUploading,
    uploadError,
    uploadedDocument,
    isProcessing,
    processingStatus,
    processingError,
    processingResult,
    uploadFile,
    retryProcessing,
    resetState
  } = useDocumentProcessing();

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      uploadFile(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      uploadFile(files[0]);
    }
    // Clear the input so the same file can be selected again
    e.target.value = '';
  };

  const contentClassName = children
    ? 'workspace-content workspace-content--custom'
    : 'workspace-content';

  return (
    <div className="center-workspace">
      <div className={contentClassName}>
        {children ? children : (
          <>
            {/* Upload Error Display */}
            {uploadError && (
              <Alert severity="error" sx={{ mb: 2, maxWidth: 600, mx: 'auto' }}>
                {uploadError}
              </Alert>
            )}

            {/* Processing Status */}
            {currentStep === 'processing' && processingStatus && (
              <ProcessingStatus 
                status={processingStatus}
                isProcessing={isProcessing}
                error={processingError}
                onRetry={() => uploadedDocument && retryProcessing(uploadedDocument.document_id)}
              />
            )}

            {/* Results Display */}
            {currentStep === 'completed' && processingResult && (
              <ResultDisplay 
                result={processingResult}
                onStartNew={resetState}
              />
            )}

            {/* Upload Interface */}
            {(currentStep === 'upload' || currentStep === 'error') && (
              <div className="upload-workspace">
            <div 
              className={`upload-zone ${dragActive ? 'drag-active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div className="upload-icon">
                <CloudUpload />
              </div>
              <h3>Upload CV Document</h3>
              <p>Drag and drop your CV file here, or click to browse</p>
              <input 
                type="file" 
                id="file-upload" 
                accept=".pdf,.docx"
              disabled={isUploading}
                onChange={handleFileSelect}
                hidden
              />
              <label htmlFor="file-upload" className={`upload-button ${isUploading ? 'uploading' : ''}`}>
                {isUploading ? 'Uploading...' : 'Choose File'}
              </label>
              <div className="supported-formats">
                <span>Supported formats: PDF, DOC, DOCX</span>
              </div>
            </div>

            <div className="process-flow">
              <div className="flow-step">
                <div className="step-icon">
                  <Description />
                </div>
                <div className="step-content">
                  <h4>1. Upload Document</h4>
                  <p>Select your CV file from your device</p>
                </div>
              </div>
              
              <div className="flow-arrow">
                <DragIndicator />
              </div>
              
              <div className="flow-step">
                <div className="step-icon">
                  <AutoAwesome />
                </div>
                <div className="step-content">
                  <h4>2. AI Processing</h4>
                  <p>Intelligent redaction with Azure OpenAI</p>
                </div>
              </div>
              
              <div className="flow-arrow">
                <DragIndicator />
              </div>
              
              <div className="flow-step">
                <div className="step-icon">
                  <GetApp />
                </div>
                <div className="step-content">
                  <h4>3. Download Result</h4>
                  <p>Get your redacted PDF output</p>
                </div>
              </div>
            </div>

            <div className="security-notice">
              <Security />
              <span>All documents are processed securely with Azure AD authentication</span>
            </div>
          </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CenterWorkspace;
